/**
 * Caps the product gallery thumbnail rail at a fixed number of visible
 * thumbnails and adds chevrons to page through the rest.
 *
 * Presentation only. The rail is the theme's own
 * `.slideshow-controls__thumbnails-container`; this element never changes
 * which slide is selected, never reorders or removes thumbnails, and never
 * touches the gallery's own click handling — it constrains the rail's size
 * and scrolls it. Without JS the rail keeps the theme's default behaviour
 * (a plain scrollable list), so this is purely additive.
 *
 * Orientation follows the rail: a column rail gets up/down chevrons, a row
 * rail gets left/right.
 */

const VISIBLE = 5;

class PdpThumbScroller extends HTMLElement {
  #rail = null;
  #list = null;
  #prev = null;
  #next = null;
  #resizeObserver = null;

  connectedCallback() {
    // The rail lives inside the gallery block, a sibling subtree in the same
    // section, so scope the lookup to the section rather than the document.
    const scope = this.closest('.shopify-section, .product-information') ?? document;
    this.#rail = scope.querySelector('.slideshow-controls__thumbnails-container');
    this.#list = this.#rail?.querySelector('.slideshow-controls__thumbnails');
    if (!this.#rail || !this.#list) return;

    const thumbs = this.#list.children;
    if (thumbs.length <= VISIBLE) return; // nothing to page through

    this.#rail.dataset.gcPaged = 'true';
    this.#build();
    this.#measure();

    this.#rail.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(() => {
      this.#measure();
      this.#update();
    });
    this.#resizeObserver.observe(this.#rail);
    this.#update();
  }

  disconnectedCallback() {
    this.#rail?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
    this.#prev?.remove();
    this.#next?.remove();
    if (this.#rail) delete this.#rail.dataset.gcPaged;
  }

  get #vertical() {
    return getComputedStyle(this.#list).flexDirection === 'column';
  }

  #build() {
    const wrap = this.#rail.parentElement;
    if (!wrap) return;
    wrap.dataset.gcThumbPager = 'true';

    this.#prev = this.#button('prev');
    this.#next = this.#button('next');
    wrap.insertBefore(this.#prev, this.#rail);
    wrap.insertBefore(this.#next, this.#rail.nextSibling);
  }

  #button(dir) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `gc-thumb-chevron gc-thumb-chevron--${dir}`;
    b.setAttribute('aria-label', dir === 'prev' ? 'Previous thumbnails' : 'More thumbnails');
    b.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="16" height="16">' +
      '<path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    b.addEventListener('click', (event) => {
      // The rail sits inside the product form; never let a chevron submit it.
      event.preventDefault();
      this.#page(dir === 'prev' ? -1 : 1);
    });
    return b;
  }

  /** Size the rail to exactly VISIBLE thumbnails, gaps included. */
  #measure() {
    // The desktop rail is display:none below 750px, where every box measures 0
    // and the flex axis reads as the initial value. Measuring then would cache
    // a bogus size, so wait until it has layout again.
    if (!this.#rail.getClientRects().length) return;

    const thumbs = [...this.#list.children].slice(0, VISIBLE);
    if (!thumbs.length) return;

    const vertical = this.#vertical;
    const gap = parseFloat(getComputedStyle(this.#list).gap) || 0;
    const extent = thumbs.reduce((total, el) => {
      const box = el.getBoundingClientRect();
      return total + (vertical ? box.height : box.width);
    }, 0);

    // The list carries its own padding inside the rail — keep it in the budget
    // so the fifth thumbnail isn't clipped.
    const listStyle = getComputedStyle(this.#list);
    const pad = vertical
      ? parseFloat(listStyle.paddingBlockStart) + parseFloat(listStyle.paddingBlockEnd)
      : parseFloat(listStyle.paddingInlineStart) + parseFloat(listStyle.paddingInlineEnd);

    const size = `${Math.round(extent + gap * (thumbs.length - 1) + (pad || 0))}px`;
    this.#rail.style.setProperty(vertical ? '--gc-thumbs-max-height' : '--gc-thumbs-max-width', size);
    this.#rail.dataset.gcAxis = vertical ? 'vertical' : 'horizontal';
  }

  #page(direction) {
    const vertical = this.#vertical;
    const amount = (vertical ? this.#rail.clientHeight : this.#rail.clientWidth) * direction;
    this.#rail.scrollBy({
      [vertical ? 'top' : 'left']: amount,
      behavior: 'smooth',
    });
  }

  #update = () => {
    if (!this.#prev || !this.#next) return;
    const vertical = this.#vertical;
    const pos = vertical ? this.#rail.scrollTop : this.#rail.scrollLeft;
    const max = vertical
      ? this.#rail.scrollHeight - this.#rail.clientHeight
      : this.#rail.scrollWidth - this.#rail.clientWidth;

    this.#prev.disabled = pos <= 1;
    this.#next.disabled = pos >= max - 1;
  };
}

if (!customElements.get('pdp-thumb-scroller')) {
  customElements.define('pdp-thumb-scroller', PdpThumbScroller);
}
